import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InTransitLine,
  PaginatedResult,
  StockTransferDetail,
  StockTransferStatus,
  StockTransferSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { inventoryKeys } from './inventory.api';

export interface TransferListParams {
  page?: number;
  pageSize?: number;
  status?: StockTransferStatus | '';
  search?: string;
}

export interface TransferLineInput {
  variantId: string;
  batchId?: string;
  quantity: number;
  notes?: string;
}

export const transferKeys = {
  all: ['transfers'] as const,
  list: (params: TransferListParams) => [...transferKeys.all, 'list', params] as const,
  detail: (id: string) => [...transferKeys.all, 'detail', id] as const,
  inTransit: (direction: 'in' | 'out') => [...transferKeys.all, 'in-transit', direction] as const,
};

export function useTransfersQuery(params: TransferListParams) {
  return useQuery({
    queryKey: transferKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<StockTransferSummary>>('/transfers', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.status ? { status: params.status } : {}),
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useTransferQuery(id: string | undefined) {
  return useQuery({
    queryKey: transferKeys.detail(id ?? ''),
    queryFn: () => api.get<StockTransferDetail>(`/transfers/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useInTransitQuery(direction: 'in' | 'out') {
  return useQuery({
    queryKey: transferKeys.inTransit(direction),
    queryFn: () => api.get<InTransitLine[]>('/transfers/in-transit', { params: { direction } }),
  });
}

/**
 * A transfer changes stock at two branches, so both the transfer views and the stock views go
 * stale together. Invalidating the whole inventory key is cheaper than reasoning about which
 * branch's cache entries were touched.
 */
function useTransferInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: transferKeys.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
  };
}

export function useCreateTransferMutation() {
  const invalidate = useTransferInvalidation();

  return useMutation({
    mutationFn: (payload: {
      destinationBranchId: string;
      lines: TransferLineInput[];
      notes?: string;
    }) => api.post<StockTransferDetail>('/transfers', payload),
    onSuccess: invalidate,
  });
}

/** Sends the shipment. Carries the version the screen showed, so a stale screen is refused, and an idempotency key, so a double click or a retry after a lost response books the stock out once. */
export function useDispatchTransferMutation() {
  const invalidate = useTransferInvalidation();

  return useIdempotentMutation<StockTransferDetail, { id: string; version: number }>(
    (payload, config) =>
      api.post<StockTransferDetail>(
        `/transfers/${payload.id}/dispatch`,
        { version: payload.version },
        config,
      ),
    { onSuccess: invalidate },
  );
}

export interface ReceiveTransferPayload {
  id: string;
  /** The version the screen showed. */
  version: number;
  /** Quantity arriving in this receipt, per line — not the running total. */
  lines: { lineId: string; receivedQuantity: number; notes?: string }[];
  /** Close the transfer despite a difference, recording it as a variance. */
  finalize?: boolean;
  notes?: string;
}

/** Counts a delivery in, once: the same key goes back with a retry, and a stale screen is refused. */
export function useReceiveTransferMutation() {
  const invalidate = useTransferInvalidation();

  return useIdempotentMutation<StockTransferDetail, ReceiveTransferPayload>(
    (payload, config) =>
      api.post<StockTransferDetail>(
        `/transfers/${payload.id}/receive`,
        {
          version: payload.version,
          lines: payload.lines,
          ...(payload.finalize ? { finalize: true } : {}),
          ...(payload.notes ? { notes: payload.notes } : {}),
        },
        config,
      ),
    { onSuccess: invalidate },
  );
}

export function useCancelTransferMutation() {
  const invalidate = useTransferInvalidation();

  return useMutation({
    mutationFn: (payload: { id: string; version: number; reason: string }) =>
      api.post<StockTransferDetail>(`/transfers/${payload.id}/cancel`, {
        version: payload.version,
        reason: payload.reason,
      }),
    onSuccess: invalidate,
  });
}
